import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Clock, Download, Minus, Plus, PoundSterling } from "lucide-react";
import { SHORT_TERM_PACKAGES, shortTermQuote, type ShortTermMonths } from "@/lib/shortTermPricing";
import { formatCurrency } from "@/lib/utils";

interface Props {
  cameras: number;
  imagesPerDay: number;
  months: ShortTermMonths;
  onChange: (v: { cameras: number; imagesPerDay: number; months: ShortTermMonths }) => void;
  onSubmit: () => void;
  submitting: boolean;
  isEdit: boolean;
  accessoriesCard?: ReactNode;
  accessoriesCost?: number;
  onDownloadPdf?: () => void;
}

export function ShortTermCalculator({ cameras, imagesPerDay, months, onChange, onSubmit, submitting, isEdit, accessoriesCard, accessoriesCost = 0, onDownloadPdf }: Props) {
  const q = shortTermQuote(cameras, imagesPerDay, months);
  const set = (p: Partial<{ cameras: number; imagesPerDay: number; months: ShortTermMonths }>) =>
    onChange({ cameras, imagesPerDay, months, ...p });

  const optionBtn = (active: boolean) =>
    `rounded-lg border p-3 text-left transition-colors ${active ? "border-foreground bg-component-background" : "border-border bg-card hover:bg-muted"}`;

  const row = (label: string, perCam: number, total: number) => (
    <div className="flex items-center justify-between border-b border-border py-2 last:border-b-0">
      <span className="text-sm font-medium">{label}</span>
      <div className="text-right">
        <div className="font-semibold">{formatCurrency(total)}</div>
        <div className="text-xs text-muted-foreground">{formatCurrency(perCam)} per camera</div>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader className="bg-component-background border-b border-foreground rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-lg"><Camera className="h-5 w-5" />1. Number of cameras</CardTitle>
          </CardHeader>
          <CardContent className="p-6 flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => set({ cameras: Math.max(1, cameras - 1) })}><Minus className="h-4 w-4" /></Button>
            <Input type="number" min={1} className="w-24 text-center" value={cameras}
              onChange={(e) => set({ cameras: Math.max(1, parseInt(e.target.value) || 1) })} />
            <Button variant="outline" size="icon" onClick={() => set({ cameras: cameras + 1 })}><Plus className="h-4 w-4" /></Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-component-background border-b border-foreground rounded-t-lg">
            <CardTitle className="text-lg">2. Images per day</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {SHORT_TERM_PACKAGES.map((p) => (
              <button key={p.name} type="button" className={optionBtn(p.imagesPerDay === imagesPerDay)} onClick={() => set({ imagesPerDay: p.imagesPerDay })}>
                <div className="text-2xl font-bold">{p.imagesPerDay}</div>
                <div className="text-xs text-muted-foreground">images/day · {p.name}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-component-background border-b border-foreground rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-lg"><Clock className="h-5 w-5" />3. Term</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-2 gap-3">
            {([3, 12] as ShortTermMonths[]).map((m) => (
              <button key={m} type="button" className={optionBtn(m === months)} onClick={() => set({ months: m })}>
                <div className="text-lg font-bold">{m} months</div>
                <div className="text-xs text-muted-foreground">{m === 3 ? 13 : 52} weeks</div>
              </button>
            ))}
          </CardContent>
        </Card>

        {accessoriesCard}
      </div>

      <div className="space-y-4">
        <Card className="lg:sticky lg:top-0">
          <CardHeader className="bg-component-background border-b border-foreground rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-lg"><PoundSterling className="h-5 w-5" />Short Term cost</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="text-sm text-muted-foreground">
              {q.cameras} camera{q.cameras > 1 ? "s" : ""} · {imagesPerDay} images/day · {months} months
            </div>
            <div>
              {row("Weekly", q.weeklyPerCamera, q.weekly)}
              {row("Quarterly", q.quarterlyPerCamera, q.quarterly)}
              {row(months === 3 ? "Annual equivalent" : "Annual", q.annualPerCamera, q.annual)}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Contract total images</span>
              <span className="font-semibold">{q.totalImages.toLocaleString("en-GB")}</span>
            </div>
            <div className="rounded-lg bg-component-background p-3 flex items-center justify-between">
              <span className="font-semibold">Contract total ({months} months)</span>
              <span className="text-xl font-bold">{formatCurrency(q.contractTotal)}</span>
            </div>
            {accessoriesCost > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span>Accessories (one-off)</span>
                  <span className="font-semibold">{formatCurrency(accessoriesCost)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="font-semibold">Total incl. accessories</span>
                  <span className="text-xl font-bold">{formatCurrency(q.contractTotal + accessoriesCost)}</span>
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground">Camera included. Prices exclude VAT.</p>
            {onDownloadPdf && (
              <Button variant="outline" onClick={onDownloadPdf} className="w-full font-medium">
                <Download className="h-4 w-4 mr-2" />
                Download Quotation PDF
              </Button>
            )}
            <Button onClick={onSubmit} disabled={submitting} className="w-full font-semibold">
              {submitting ? (isEdit ? "Updating Opportunity..." : "Creating Opportunity...") : (isEdit ? "Update Opportunity" : "Create Opportunity")}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
